import axios from 'axios';
import type { AnnotationPreset } from '@rta/rta-blueprint-component';

export type RecentEntryKind = 'design' | 'package';

// Mirrors the API's RecentEntryData (apps/api/src/domain/get-user-preferences.ts)
export type RecentEntry = {
	kind: RecentEntryKind,
	id: string,
	customerEmail: string,
	label: string,
	openedAt: string
};

export type UserPreferencesResponse = {
	email: string,
	annotationPresets: Array<AnnotationPreset>,
	recents: Array<RecentEntry>
};

export type GetUserPreferencesParams = {
	getToken: () => Promise<string>,
	setState?: (data: UserPreferencesResponse) => void,
	setError?: (error: string) => void
};

// Scoped server-side to the caller's own identity from the JWT -- no email param, so this can
// never read another user's preferences (see apps/api endpoint-operations/get-user-preferences.epo.ts).
export const getUserPreferences = async (params: GetUserPreferencesParams): Promise<[UserPreferencesResponse | null, string | null]> => {
	const { getToken, setState, setError } = params;

	try {
		const token = await getToken();
		const response = await axios.get(`${import.meta.env.VITE_API_URL}/preferences`, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		});

		// A build of the API without the #354 routes answers 204 with an empty body, which is not an error to axios
		if (response.status !== 200 || !response.data?.annotationPresets) {
			console.error('[get-user-preferences] unexpected response', response.status, response.data);
			const message = `Error loading preferences (status ${response.status})`;
			if (setError) setError(message);
			return [null, message];
		}

		if (setState) setState(response.data);
		return [response.data, null];
	} catch (error) {
		console.error('[get-user-preferences] request failed', error);
		if (setError) setError('Error loading preferences');
		return [null, 'Error loading preferences'];
	}
};
